import { createGlobalStyle } from 'styled-components';
import { colorSystem, spacingSystem } from './colorSystem';

const GlobalStyles = createGlobalStyle`
  :root {
    /* Legacy Colors (for compatibility) */
    --davinci-blue: ${colorSystem.accent.primary};
    --davinci-grey: ${colorSystem.text.secondary};
    --davinci-cloud: ${colorSystem.secondary.light};
    --davinci-stone: ${colorSystem.secondary.base};
    --davinci-midnight: ${colorSystem.accent.dark};
    --davinci-denim: ${colorSystem.accent.secondary};
    --davinci-beach: ${colorSystem.accent.light};
    --white: ${colorSystem.primary.base};
    --black: #000000;
    --light-grey: ${colorSystem.primary.dark};
    
    /* New Color System (60-30-10 Rule) */
    /* Primary (60%) */
    --color-primary-base: ${colorSystem.primary.base};
    --color-primary-light: ${colorSystem.primary.light};
    --color-primary-dark: ${colorSystem.primary.dark};
    
    /* Secondary (30%) */
    --color-secondary-lightest: ${colorSystem.secondary.lightest};
    --color-secondary-lighter: ${colorSystem.secondary.lighter};
    --color-secondary-light: ${colorSystem.secondary.light};
    --color-secondary-base: ${colorSystem.secondary.base};
    --color-secondary-dark: ${colorSystem.secondary.dark};
    --color-secondary-darker: ${colorSystem.secondary.darker};
    --color-secondary-darkest: ${colorSystem.secondary.darkest};
    
    /* Accent (10%) */
    --color-accent-primary: ${colorSystem.accent.primary};
    --color-accent-secondary: ${colorSystem.accent.secondary};
    --color-accent-light: ${colorSystem.accent.light};
    --color-accent-dark: ${colorSystem.accent.dark};
    
    /* Text Colors */
    --text-primary: ${colorSystem.text.primary};
    --text-secondary: ${colorSystem.text.secondary};
    --text-muted: ${colorSystem.text.muted};
    --text-light: ${colorSystem.text.light};
    --text-inverse: ${colorSystem.text.inverse};
    
    /* Borders */
    --border-light: ${colorSystem.borders.light};
    --border-default: ${colorSystem.borders.default};
    --border-dark: ${colorSystem.borders.dark};
    
    /* System/Status Colors */
    --color-success: ${colorSystem.support.success};
    --color-warning: ${colorSystem.support.warning};
    --color-error: ${colorSystem.support.error};
    --color-info: ${colorSystem.support.info};
    
    /* Typography - System Font Stack for better performance */
    --font-primary: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', system-ui, 'Helvetica Neue', Arial, sans-serif;
    --font-weight-light: 300;
    --font-weight-medium: 500;
    --font-weight-bold: 700;
    --font-weight-extra-bold: 800;
    
    /* Modern Type Scale with Fluid Typography (16px base, 1.333 ratio) */
    --fs-base: clamp(1rem, 0.95rem + 0.25vw, 1.125rem); /* 16-18px */
    --fs-xs: clamp(0.75rem, 0.72rem + 0.15vw, 0.875rem); /* 12-14px */
    --fs-sm: clamp(0.875rem, 0.85rem + 0.125vw, 1rem); /* 14-16px */
    --fs-md: var(--fs-base);
    --fs-lg: clamp(1.125rem, 1.08rem + 0.225vw, 1.333rem); /* 18-21px */
    --fs-xl: clamp(1.333rem, 1.25rem + 0.415vw, 1.777rem); /* 21-28px */
    --fs-2xl: clamp(1.777rem, 1.65rem + 0.635vw, 2.369rem); /* 28-38px */
    --fs-3xl: clamp(2.369rem, 2.15rem + 1.095vw, 3.157rem); /* 38-50px */
    --fs-4xl: clamp(2.827rem, 2.5rem + 1.635vw, 4.209rem); /* 45-67px */
    --fs-5xl: clamp(3.157rem, 2.8rem + 1.785vw, 4.5rem); /* 50-72px */
    --fs-6xl: clamp(3.583rem, 3.2rem + 1.915vw, 5rem); /* 57-80px */
    
    /* Line Heights (Optimized for readability) */
    --lh-tight: 1.2;
    --lh-snug: 1.4;
    --lh-normal: 1.6;
    --lh-relaxed: 1.8;
    --lh-loose: 2.0;
    
    /* Letter Spacing */
    --ls-tighter: -0.05em;
    --ls-tight: -0.025em;
    --ls-normal: 0;
    --ls-wide: 0.025em;
    --ls-wider: 0.05em;
    --ls-widest: 0.1em;
    
    /* Spacing (Increased for better breathing room) */
    --spacing-xs: ${spacingSystem.xs};
    --spacing-sm: ${spacingSystem.sm};
    --spacing-md: ${spacingSystem.md};
    --spacing-lg: ${spacingSystem.lg};
    --spacing-xl: ${spacingSystem.xl};
    --spacing-2xl: ${spacingSystem['2xl']};
    --spacing-3xl: ${spacingSystem['3xl']};
    --spacing-4xl: ${spacingSystem['4xl']};
    --spacing-5xl: ${spacingSystem['5xl']};
    --spacing-section: ${spacingSystem.section};
    --spacing-section-mobile: ${spacingSystem.sectionMobile};
    
    /* Breakpoints */
    --mobile-max: 767px;
    --tablet-min: 768px;
    --tablet-max: 1023px;
    --desktop-min: 1024px;
    --wide-min: 1440px;
    
    /* Simplified Shadow System - Only 3 levels */
    --shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    
    /* Transitions */
    --transition-fast: 150ms ease-in-out;
    --transition-base: 250ms ease-in-out;
    --transition-slow: 350ms ease-in-out;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
  }

  body {
    font-family: var(--font-primary);
    font-size: var(--fs-base);
    font-weight: var(--font-weight-medium);
    color: var(--text-primary);
    background-color: var(--white);
    line-height: var(--lh-normal);
    letter-spacing: var(--ls-normal);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    position: relative;
  }

  body::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: -1;
    opacity: 0.02;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cdefs%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3C/defs%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
    background-repeat: repeat;
    background-size: 150px 150px;
    mix-blend-mode: multiply;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: var(--font-weight-bold);
    line-height: var(--lh-tight);
    color: var(--davinci-grey);
    margin-bottom: var(--spacing-md);
    letter-spacing: var(--ls-tight);
  }

  h1 {
    font-size: var(--fs-6xl);
    font-weight: var(--font-weight-extra-bold);
    line-height: var(--lh-tight);
  }

  h2 {
    font-size: var(--fs-5xl);
    line-height: var(--lh-snug);
  }

  h3 {
    font-size: var(--fs-4xl);
    line-height: var(--lh-snug);
  }

  h4 {
    font-size: var(--fs-3xl);
    line-height: var(--lh-normal);
  }
  
  h5 {
    font-size: var(--fs-xl);
    line-height: var(--lh-normal);
    font-weight: var(--font-weight-medium);
  }
  
  h6 {
    font-size: var(--fs-lg);
    line-height: var(--lh-normal);
    font-weight: var(--font-weight-medium);
    letter-spacing: var(--ls-normal);
  }

  p {
    font-size: var(--fs-base);
    line-height: var(--lh-relaxed);
    margin-bottom: var(--spacing-md);
  }
  
  .lead {
    font-size: var(--fs-lg);
    line-height: var(--lh-relaxed);
    font-weight: var(--font-weight-light);
  }
  
  small, .small {
    font-size: var(--fs-sm);
    line-height: var(--lh-normal);
  }

  a {
    color: var(--davinci-blue);
    text-decoration: none;
    transition: color var(--transition-fast);
    
    &:hover {
      color: var(--davinci-denim);
    }
  }

  button {
    font-family: var(--font-primary);
    font-size: var(--fs-base);
    font-weight: var(--font-weight-medium);
    letter-spacing: var(--ls-wide);
    cursor: pointer;
    transition: all var(--transition-base);
  }
  
  label {
    font-size: var(--fs-sm);
    font-weight: var(--font-weight-medium);
    letter-spacing: var(--ls-wide);
    text-transform: uppercase;
    margin-bottom: var(--spacing-xs);
    display: inline-block;
  }
  
  input, textarea, select {
    font-family: var(--font-primary);
    font-size: var(--fs-base);
    line-height: var(--lh-normal);
  }
  
  blockquote {
    font-size: var(--fs-lg);
    line-height: var(--lh-relaxed);
    font-style: italic;
    font-weight: var(--font-weight-light);
    padding-left: var(--spacing-lg);
    border-left: 4px solid var(--davinci-blue);
    margin: var(--spacing-xl) 0;
  }
  
  code {
    font-size: var(--fs-sm);
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    background-color: var(--light-grey);
    padding: 0.125em 0.25em;
    border-radius: 0.25em;
  }
  
  pre {
    font-size: var(--fs-sm);
    line-height: var(--lh-normal);
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    background-color: var(--light-grey);
    padding: var(--spacing-md);
    border-radius: 0.5em;
    overflow-x: auto;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  ul, ol {
    list-style-position: inside;
    margin-bottom: var(--spacing-md);
  }

  li {
    margin-bottom: var(--spacing-xs);
  }

  /* Utility Classes */
  .container {
    width: 100%;
    max-width: 1400px;  /* Increased for better layout */
    margin: 0 auto;
    padding: 0 var(--spacing-xl);  /* 32px on mobile */
    
    @media (min-width: 768px) {
      padding: 0 var(--spacing-2xl);  /* 40px on tablet */
    }
    
    @media (min-width: 1024px) {
      padding: 0 var(--spacing-3xl);  /* 48px on desktop */
    }
    
    @media (min-width: 1600px) {
      padding: 0 var(--spacing-4xl);  /* 64px on large screens */
    }
  }

  .text-center {
    text-align: center;
  }

  .text-left {
    text-align: left;
  }

  .text-right {
    text-align: right;
  }

  .text-primary {
    color: var(--davinci-blue);
  }

  .text-secondary {
    color: var(--davinci-grey);
  }
  
  /* Typography Utility Classes */
  .text-xs { font-size: var(--fs-xs); }
  .text-sm { font-size: var(--fs-sm); }
  .text-md { font-size: var(--fs-md); }
  .text-lg { font-size: var(--fs-lg); }
  .text-xl { font-size: var(--fs-xl); }
  .text-2xl { font-size: var(--fs-2xl); }
  .text-3xl { font-size: var(--fs-3xl); }
  .text-4xl { font-size: var(--fs-4xl); }
  .text-5xl { font-size: var(--fs-5xl); }
  .text-6xl { font-size: var(--fs-6xl); }
  
  /* Line Height Utilities */
  .leading-tight { line-height: var(--lh-tight); }
  .leading-snug { line-height: var(--lh-snug); }
  .leading-normal { line-height: var(--lh-normal); }
  .leading-relaxed { line-height: var(--lh-relaxed); }
  .leading-loose { line-height: var(--lh-loose); }
  
  /* Letter Spacing Utilities */
  .tracking-tighter { letter-spacing: var(--ls-tighter); }
  .tracking-tight { letter-spacing: var(--ls-tight); }
  .tracking-normal { letter-spacing: var(--ls-normal); }
  .tracking-wide { letter-spacing: var(--ls-wide); }
  .tracking-wider { letter-spacing: var(--ls-wider); }
  .tracking-widest { letter-spacing: var(--ls-widest); }
  
  /* Font Weight Utilities */
  .font-light { font-weight: var(--font-weight-light); }
  .font-medium { font-weight: var(--font-weight-medium); }
  .font-bold { font-weight: var(--font-weight-bold); }
  .font-extrabold { font-weight: var(--font-weight-extra-bold); }

  .bg-primary {
    background-color: var(--davinci-blue);
  }

  .bg-secondary {
    background-color: var(--davinci-grey);
  }

  .bg-light {
    background-color: var(--light-grey);
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes slideDown {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

export default GlobalStyles;